/**
 * A-11 保存済みセット `/sets`（M1.9・FR-132・D-100）＝**「いつもの注文」をワンタップでカートへ**。
 *
 * ★セットが持つのは **tenant_id・product_id・数量・名前だけ**＝適用のたびに
 *   **現在のカタログへ再解決**する（FR-44「同じ内容で再注文」と同じ規則）。
 *   だから金額は「いまのお取引単価での概算」で、保存時の値段は存在しない。
 * ★**取扱終了・未接続・停止中の卸の品はカートに入れず、その場で知らせる**（黙って欠けさせない）。
 * ★削除はハード DELETE（D-100⑤）＝ConfirmDialog 必須（復元の概念が無い）。
 * ★保存の入口はカート（「このカートをセットとして保存」＝Cart.tsx）。ここでは作れない＝
 *   セットは「実際に組んだカート」から生まれるのが正（空のセットを手で組み立てる画面を作らない）。
 */

import type { ConnectedTenant, PartnerProduct, SavedSet, partnerApi } from "@kenzai/api-client";
import { Card, ConfirmDialog, Empty, ErrorNote, Money, Skeleton } from "@kenzai/ui";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { useCart } from "../cart";
import { useCartUi } from "../cartUi";
import { PageTitle } from "../Shell";

type Client = ReturnType<typeof partnerApi>;

export function SavedSets({ client, connections }: { client: Client; connections: ConnectedTenant[] }) {
  const cart = useCart();
  const ui = useCartUi();
  const qc = useQueryClient();
  const usable = connections.filter((c) => c.tenant_status !== "suspended");

  const sets = useQuery({ queryKey: ["saved-sets"], queryFn: () => client.savedSets() });
  // 現在のカタログ（queryKey は A-01 と共有＝二重取得しない）＝概算金額と「いま買えるか」の根拠
  const catalogQueries = useQueries({
    queries: usable.map((c) => ({
      queryKey: ["catalog", c.tenant_id],
      queryFn: () => client.catalog(c.tenant_id),
    })),
  });

  /** `tenant_id:product_id` → いまカタログに載っている商品（無ければ「いまは買えない」）。 */
  const index = useMemo(() => {
    const m = new Map<string, { product: PartnerProduct; tenant: ConnectedTenant }>();
    usable.forEach((tenant, i) => {
      for (const c of catalogQueries[i]?.data ?? []) {
        for (const p of c.items) {
          const key = `${tenant.tenant_id}:${p.product_id}`;
          if (!m.has(key)) m.set(key, { product: p, tenant });
        }
      }
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useQueries の配列は毎回新しい参照
  }, [connections, catalogQueries.map((x) => x.dataUpdatedAt).join()]);

  const catalogsLoading = catalogQueries.some((x) => x.isLoading);

  const [openId, setOpenId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<SavedSet | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const del = useMutation({
    mutationFn: (id: number) => client.deleteSavedSet(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["saved-sets"] });
      setDeleting(null);
    },
  });

  /** まとめてカートに入れる＝現在のカタログに載っている品だけ（D-100②）。 */
  function apply(set: SavedSet) {
    let added = 0;
    let skipped = 0;
    for (const item of set.items) {
      const hit = index.get(`${item.tenant_id}:${item.product_id}`);
      if (!hit) {
        skipped += 1;
        continue;
      }
      cart.add(item.tenant_id, {
        product_id: hit.product.product_id,
        name: hit.product.name,
        spec: hit.product.spec,
        unit: hit.product.unit,
        unit_price: hit.product.unit_price,
        qty: Number(item.qty),
      });
      added += 1;
    }
    setNote(
      skipped === 0
        ? `${added}品目をカートに入れました。`
        : added === 0
          ? "このセットの商品は、現在どれもお取り扱いがありません。"
          : `${added}品目をカートに入れました（お取り扱いのない ${skipped} 品は入れていません）。`,
    );
  }

  if (sets.isLoading || catalogsLoading) {
    return (
      <div style={{ display: "grid", gap: "var(--kz-space-4)" }} aria-hidden="true">
        <Skeleton rows={1} height={24} />
        {[0, 1].map((i) => (
          <Card key={i}>
            <Skeleton rows={3} height={14} />
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: "var(--kz-space-4)" }} data-testid="saved-sets">
      <div>
        <PageTitle>保存済みのセット</PageTitle>
        <p style={{ margin: 0, fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
          いつもの注文の組み合わせです。まとめてカートに入れられます（金額は、いまのお取引単価で計算します）。
        </p>
      </div>

      {sets.isError && <ErrorNote error={sets.error} />}

      {note && (
        <p
          data-testid="apply-note"
          role="status"
          style={{
            margin: 0,
            padding: "var(--kz-space-3)",
            background: "var(--kz-color-primary-weak)",
            borderRadius: "var(--kz-radius-sm)",
            fontSize: "var(--kz-font-size-sm)",
            display: "flex",
            alignItems: "center",
            gap: "var(--kz-space-3)",
            flexWrap: "wrap",
          }}
        >
          {note}
          <button
            type="button"
            data-testid="apply-go-cart"
            onClick={ui.openCart}
            className="kz-pressable"
            style={{
              minHeight: 40,
              padding: "0 var(--kz-space-3)",
              border: "1px solid var(--kz-color-primary)",
              borderRadius: "var(--kz-radius-sm)",
              background: "var(--kz-color-surface)",
              color: "var(--kz-color-primary)",
              font: "inherit",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            カートを見る
          </button>
        </p>
      )}

      {(sets.data ?? []).length === 0 ? (
        <Empty
          title="保存したセットはまだありません"
          hint="カートに商品を入れて「このカートをセットとして保存」を押すと、ここに並びます。"
        />
      ) : (
        // ★PC は Design `.kp-setgrid`＝**360px 以上で自動的に段組み**（セットは横に並べて
        //   見比べるもの＝1列だと1画面に2つしか入らない）。スマホは1列＝外側のグリッドと
        //   同じ間隔（`--kz-space-4`）を器の中でも使うので、**見た目は変わらない**。
        <div className="kz-setgrid" style={{ display: "grid", gap: "var(--kz-space-4)", alignItems: "start" }}>
          {(sets.data ?? []).map((s) => (
            <SetCard
              key={s.id}
              set={s}
              index={index}
              open={openId === s.id}
              onToggle={() => setOpenId(openId === s.id ? null : s.id)}
              onApply={() => apply(s)}
              onDelete={() => setDeleting(s)}
            />
          ))}
        </div>
      )}

      {/* 作る場所は**カート**（ここでは作れない）＝一覧の末尾に常設で置く（Design `.oa-foot`）。
          空のときは `Empty` の hint が同じことを言うので出さない（同じ案内を2度書かない）。 */}
      {(sets.data ?? []).length > 0 && (
        <p className="oa-foot" style={{ margin: 0 }}>
          新しいセットは、カートの「このカートをセットとして保存」から追加できます。
        </p>
      )}

      {deleting && (
        <ConfirmDialog
          title={`「${deleting.name}」を削除しますか？`}
          body="削除すると元に戻せません（ご注文の履歴には影響しません）。"
          confirmLabel="削除する"
          tone="danger"
          busy={del.isPending}
          onConfirm={() => del.mutate(deleting.id)}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

/** セット1枚。概算は**解決できた品だけ**の合計（1品でも単価未確定なら「—」＝0円と読ませない）。 */
function SetCard({
  set,
  index,
  open,
  onToggle,
  onApply,
  onDelete,
}: {
  set: SavedSet;
  index: Map<string, { product: PartnerProduct; tenant: ConnectedTenant }>;
  open: boolean;
  onToggle: () => void;
  onApply: () => void;
  onDelete: () => void;
}) {
  const resolved = set.items.map((item) => ({ item, hit: index.get(`${item.tenant_id}:${item.product_id}`) }));
  const unavailable = resolved.filter((r) => !r.hit).length;
  const priceable = resolved.filter((r) => r.hit && r.hit.product.unit_price !== null);
  const estimate =
    priceable.length === 0 || priceable.length !== resolved.length - unavailable
      ? null
      : priceable.reduce((sum, r) => sum + Number(r.hit!.product.unit_price) * Number(r.item.qty), 0);

  return (
    // ★card の testid は `saved-set-`＝中のボタン（`set-apply-` ほか）と接頭辞を分ける
    //   （`set-` にすると `[data-testid^="set-"]` がカードとボタンを同時に掴む）
    <Card data-testid={`saved-set-${set.id}`}>
      {/* ★Design `.set-h` は名前と「N品目・約◯◯円」を同じ行に置くが、こちらの品目数は
          「1 品目 ／ 約 31,200円（税抜）」と長く、1行に収めると**名前が単語の途中で折り返す**
          （390px で実測）。名前を優先し、入らなければ品目数を次の行に落とす。 */}
      <div style={{ display: "flex", alignItems: "baseline", gap: "var(--kz-space-2)", flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, fontSize: "var(--kz-font-size-md)" }}>{set.name}</h2>
        <span
          className="kz-num"
          style={{ marginLeft: "auto", fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}
        >
          {set.items.length} 品目
          {estimate !== null && (
            <>
              {" ／ 約 "}
              <Money value={String(estimate)} />
              （税抜）
            </>
          )}
        </span>
      </div>

      {unavailable > 0 && (
        <p
          data-testid={`set-unavailable-${set.id}`}
          style={{ margin: "var(--kz-space-2) 0 0", fontSize: "var(--kz-font-size-sm)", color: "var(--kz-status-warn)" }}
        >
          {unavailable} 品は現在お取り扱いがありません（カートには入りません）。
        </p>
      )}

      {open && (
        <div
          style={{
            marginTop: "var(--kz-space-3)",
            paddingTop: "var(--kz-space-3)",
            borderTop: "1px dashed var(--kz-color-border)",
            display: "grid",
            gap: 6,
          }}
        >
          {resolved.map(({ item, hit }, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "var(--kz-space-3)",
                fontSize: "var(--kz-font-size-sm)",
                color: hit ? "inherit" : "var(--kz-color-ink-muted)",
              }}
            >
              <span style={{ minWidth: 0 }}>
                {hit ? hit.product.name : "お取り扱いのない商品"}
                <span className="kz-num" style={{ color: "var(--kz-color-ink-muted)" }}>
                  {` × ${Number(item.qty)}${hit ? hit.product.unit : ""}`}
                </span>
                {hit && (
                  <span style={{ color: "var(--kz-color-ink-muted)" }}>{`（${hit.tenant.tenant_name}）`}</span>
                )}
              </span>
              <span className="kz-num" style={{ flex: "none" }}>
                {hit ? (
                  <Money
                    value={
                      hit.product.unit_price === null
                        ? null
                        : String(Number(hit.product.unit_price) * Number(item.qty))
                    }
                  />
                ) : (
                  "—"
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: "var(--kz-space-2)", marginTop: "var(--kz-space-3)", flexWrap: "wrap" }}>
        <button
          type="button"
          data-testid={`set-apply-${set.id}`}
          onClick={onApply}
          disabled={set.items.length === unavailable}
          className="kz-btn kz-btn--accent kz-pressable"
          style={{ flex: 1, minHeight: 48, fontWeight: 700, minWidth: 200 }}
        >
          まとめてカートに入れる
        </button>
        <button
          type="button"
          data-testid={`set-toggle-${set.id}`}
          onClick={onToggle}
          className="kz-pressable"
          style={{
            minHeight: 48,
            padding: "0 var(--kz-space-4)",
            border: "1px solid var(--kz-color-border)",
            borderRadius: "var(--kz-radius-sm)",
            background: "var(--kz-color-surface)",
            font: "inherit",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {open ? "閉じる" : "内容を見る"}
        </button>
        <button
          type="button"
          data-testid={`set-delete-${set.id}`}
          onClick={onDelete}
          aria-label={`${set.name} を削除`}
          className="kz-pressable"
          style={{
            minHeight: 48,
            padding: "0 var(--kz-space-3)",
            border: "1px solid var(--kz-color-border)",
            borderRadius: "var(--kz-radius-sm)",
            background: "var(--kz-color-surface)",
            color: "var(--kz-status-danger)",
            font: "inherit",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          削除
        </button>
      </div>
    </Card>
  );
}
